/**
 * PhotoCaptureManager - Reusable component for photo upload/capture
 * Features:
 * - Webcam capture with camera switching
 * - File upload from disk
 * - Staging model: no Drive I/O until commitPhotos() is called
 * - Display thumbnails: existing (server-loaded) + staged (pending upload)
 * - Deferred delete: marked locally, applied on commit
 */

import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import { Modal, Button, Space, Empty, Spin, message, Upload, Tooltip, theme } from 'antd'
import {
  CameraOutlined, DeleteOutlined, CloudUploadOutlined, SwapOutlined,
  CloseOutlined, CheckOutlined, ReloadOutlined,
} from '@ant-design/icons'
import axiosInstance from '@/lib/axios'
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage'

interface WorkOrderPhoto {
  id: number
  file_name: string
  google_drive_url: string
  thumbnail_url?: string
  source: 'webcam' | 'upload'
  created_at: string
}

interface StagedFile {
  id: string
  file: File
  preview: string   // URL.createObjectURL for file uploads, base64 dataURL for webcam
  source: 'file' | 'webcam'
}

export interface PhotoCaptureManagerHandle {
  openWebcamModal: () => void
  openUploadModal: () => void
  commitPhotos: () => Promise<void>
  discardChanges: () => void
}

interface PhotoCaptureManagerProps {
  workOrderId: number
  onPhotoAdded?: () => void
  compact?: boolean
  /** Fase fotografica corrente. Default: 'accettazione'. */
  fase?: 'accettazione' | 'lavorazione' | 'riconsegna'
  /** ID dell'intervento collegato alla foto. Default: null. */
  interventionId?: number | null
  /** Nasconde i pulsanti Scatta, Carica ed Elimina. Default: false. */
  readOnly?: boolean
}

// ─── ExistingPhotoCard ────────────────────────────────────────────────────────
// Sub-componente per ogni miniatura di foto già salvata sul server.
// Usa useAuthenticatedImage per scaricare via axiosInstance (con token JWT),
// così il gate work_order_photos.view funziona senza rompere il layout.
interface ExistingPhotoCardProps {
  photo: WorkOrderPhoto
  committing: boolean
  readOnly?: boolean
  onSelect: () => void
  onDelete: (e: React.MouseEvent) => void
}
function ExistingPhotoCard({ photo, committing, readOnly = false, onSelect, onDelete }: ExistingPhotoCardProps) {
  const { token } = theme.useToken()
  const thumbnailUrl = photo.thumbnail_url || photo.google_drive_url
  const { src: blobSrc, loading: imgLoading, error: imgError } = useAuthenticatedImage(thumbnailUrl)

  return (
    <div
      style={{
        position: 'relative',
        width: '70px',
        height: '70px',
        flexShrink: 0,
        borderRadius: '6px',
        overflow: 'hidden',
        border: '1px solid #e8e8e8',
        cursor: 'pointer',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onSelect}
    >
      {imgLoading && <Spin size="small" style={{ position: 'absolute' }} />}
      {imgError && (
        <span style={{ position: 'absolute', fontSize: 18, color: '#ccc' }} title="Immagine non disponibile">⚠️</span>
      )}
      {blobSrc && !imgError && (
        <img
          src={blobSrc}
          alt={photo.file_name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: token.colorBgMask,
          color: token.colorTextLightSolid,
          fontSize: '9px',
          textAlign: 'center',
          padding: '1px 0',
          lineHeight: '14px',
          pointerEvents: 'none',
        }}
      >
        #{photo.id}
      </div>
      {!readOnly && (
        <Tooltip title="Rimuovi">
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            disabled={committing}
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              padding: '2px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '4px',
            }}
            onClick={onDelete}
          />
        </Tooltip>
      )}
    </div>
  )
}
// ─────────────────────────────────────────────────────────────────────────────

const PhotoCaptureManagerComponent: React.ForwardRefRenderFunction<PhotoCaptureManagerHandle, PhotoCaptureManagerProps> =
  ({ workOrderId, onPhotoAdded, compact = false, fase = 'accettazione', interventionId = null, readOnly = false }, ref) => {

  const [webcamModalVisible, setWebcamModalVisible] = useState(false)
  const [uploadModalVisible, setUploadModalVisible] = useState(false)
  const [existingPhotos, setExistingPhotos] = useState<WorkOrderPhoto[]>([])
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([])
  const [photosToDelete, setPhotosToDelete] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [cameraFacing, setCameraFacing] = useState<'user' | 'environment'>('user')
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight)
  const [viewerModalVisible, setViewerModalVisible] = useState(false)
  const [selectedPhotoForView, setSelectedPhotoForView] = useState<WorkOrderPhoto | null>(null)

  const { token } = theme.useToken()

  // Hook per la foto a schermo intero: scarica via axios (con token) e revoca il blob
  // alla chiusura del viewer o al cambio di foto (prevenzione memory leak, test T12).
  const viewerUrl = selectedPhotoForView?.google_drive_url ?? null
  const { src: viewerBlobSrc, loading: viewerImgLoading } = useAuthenticatedImage(viewerUrl)

  const stagedIdCounter = useRef(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    const onResize = () => setIsLandscape(window.innerWidth > window.innerHeight)
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [])

  useImperativeHandle(ref, () => ({
    openWebcamModal: () => {
      setWebcamModalVisible(true)
      setTimeout(() => startWebcam(), 100)
    },
    openUploadModal: () => setUploadModalVisible(true),
    commitPhotos,
    discardChanges,
  }))

  useEffect(() => {
    loadPhotos()
    return () => {
      streamRef.current?.getTracks().forEach(track => track.stop())
    }
  }, [workOrderId])

  useEffect(() => {
    if (webcamModalVisible && !capturedImage && capturing) {
      setTimeout(() => startWebcam(), 50)
    }
  }, [capturedImage, webcamModalVisible])

  // ==================== SERVER I/O ====================

  const loadPhotos = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get<WorkOrderPhoto[]>(`/work-orders/${workOrderId}/photos`)
      setExistingPhotos(response.data || [])
    } catch (error) {
      console.error('Failed to load photos:', error)
    } finally {
      setLoading(false)
    }
  }

  const commitPhotos = async (): Promise<void> => {
    if (stagedFiles.length === 0 && photosToDelete.length === 0) return
    setCommitting(true)

    const totalUploads = stagedFiles.length
    let completedUploads = 0

    try {
      for (const staged of stagedFiles) {
        const formData = new FormData()
        if (staged.source === 'webcam') {
          formData.append('file', staged.file, 'webcam_photo.jpg')
          formData.append('fase', fase)
          if (interventionId != null) formData.append('intervention_id', String(interventionId))
          await axiosInstance.post(
            `/work-orders/${workOrderId}/photos/upload-webcam`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
        } else {
          formData.append('files', staged.file)
          formData.append('fase', fase)
          if (interventionId != null) formData.append('intervention_id', String(interventionId))
          await axiosInstance.post(
            `/work-orders/${workOrderId}/photos/upload-file`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
        }
        // Rimuovi immediatamente dall'array di staging: così il retry non ricarica questa foto
        if (staged.source === 'file') URL.revokeObjectURL(staged.preview)
        setStagedFiles(prev => prev.filter(f => f.id !== staged.id))
        completedUploads++
      }

      for (const photoId of photosToDelete) {
        await axiosInstance.delete(`/work-orders/${workOrderId}/photos/${photoId}`)
        // Rimuovi immediatamente: così il retry non tenta un delete già applicato
        setPhotosToDelete(prev => prev.filter(id => id !== photoId))
      }

      await loadPhotos()
      onPhotoAdded?.()
    } catch (error) {
      console.error('commitPhotos failed:', error)
      if (completedUploads < totalUploads) {
        message.error(
          totalUploads > 1
            ? `Caricate ${completedUploads} di ${totalUploads} foto. Riprova per le restanti.`
            : 'Errore nel caricamento della foto. Riprova.'
        )
      } else {
        message.error("Errore nell'eliminazione di alcune foto. Riprova.")
      }
      throw error
    } finally {
      setCommitting(false)
    }
  }

  const discardChanges = () => {
    stagedFiles.forEach(f => { if (f.source === 'file') URL.revokeObjectURL(f.preview) })
    setStagedFiles([])
    setPhotosToDelete([])
  }

  // ==================== STAGING ====================

  const addFileToStaging = (file: File) => {
    const id = `staged-${++stagedIdCounter.current}`
    setStagedFiles(prev => [...prev, { id, file, preview: URL.createObjectURL(file), source: 'file' }])
  }

  const addWebcamToStaging = async () => {
    if (!capturedImage) return
    const blob = await fetch(capturedImage).then(r => r.blob())
    const file = new File([blob], 'webcam_photo.jpg', { type: 'image/jpeg' })
    const id = `staged-${++stagedIdCounter.current}`
    setStagedFiles(prev => [...prev, { id, file, preview: capturedImage!, source: 'webcam' }])
    setCapturedImage(null)  // reset to camera for next shot
  }

  const removeFromStaging = (id: string) => {
    setStagedFiles(prev => {
      const staged = prev.find(f => f.id === id)
      if (staged?.source === 'file') URL.revokeObjectURL(staged.preview)
      return prev.filter(f => f.id !== id)
    })
  }

  const markForDeletion = (photoId: number) => {
    setPhotosToDelete(prev => [...prev, photoId])
  }

  // ==================== WEBCAM ====================

  const startWebcam = async () => {
    try {
      setCapturing(true)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
    } catch (error) {
      console.error('Webcam access failed:', error)
      message.error('Impossibile accedere alla webcam')
      setCapturing(false)
    }
  }

  const stopWebcam = () => {
    streamRef.current?.getTracks().forEach(track => track.stop())
    streamRef.current = null
    setCapturedImage(null)
    setCapturing(false)
  }

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return
    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) return
    canvasRef.current.width = videoRef.current.videoWidth
    canvasRef.current.height = videoRef.current.videoHeight
    ctx.drawImage(videoRef.current, 0, 0)
    setCapturedImage(canvasRef.current.toDataURL('image/jpeg', 0.85))
  }

  const switchCamera = () => {
    setCameraFacing(prev => (prev === 'user' ? 'environment' : 'user'))
    stopWebcam()
    setTimeout(() => startWebcam(), 500)
  }

  const cancelWebcam = () => {
    stopWebcam()
    setWebcamModalVisible(false)
  }

  // ==================== RENDER ====================

  const renderWebcamModal = () => {
    const circleButtonStyle: React.CSSProperties = {
      borderRadius: '50%',
      width: '60px',
      height: '60px',
      minWidth: '60px',
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
    }

    const buttonsWrapperStyle: React.CSSProperties = isLandscape
      ? {
          position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', flexDirection: 'column', gap: '12px', zIndex: 10,
        }
      : {
          position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'row', gap: '12px', zIndex: 10,
        }

    const videoContainerStyle: React.CSSProperties = {
      width: '100vw', height: '100vh', backgroundColor: '#000',
      position: 'relative', overflow: 'hidden',
    }

    return (
      <Modal
        title=""
        open={webcamModalVisible}
        onCancel={cancelWebcam}
        destroyOnClose={true}
        width="100vw"
        height="100vh"
        footer={null}
        styles={{
          body: { padding: '0px', margin: '0px', width: '100%', height: '100%', overflow: 'hidden' },
          content: { padding: '0', margin: '0', width: '100%', height: '100%', display: 'flex', overflow: 'hidden' },
        }}
        closable={false}
        mask={true}
        centered={false}
        style={{ top: 0, left: 0, margin: 0, padding: 0, maxWidth: '100vw', maxHeight: '100vh' }}
        wrapClassName="fullscreen-modal"
      >
        {!capturedImage ? (
          <div style={videoContainerStyle}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
            />
            <div style={buttonsWrapperStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                  type="primary"
                  icon={<CameraOutlined />}
                  onClick={takePhoto}
                  style={{ ...circleButtonStyle, backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                />
                <span style={{ fontSize: 11, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', whiteSpace: 'nowrap' }}>Scatta</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                  icon={<SwapOutlined />}
                  onClick={switchCamera}
                  style={{ ...circleButtonStyle, backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
                />
                <span style={{ fontSize: 11, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', whiteSpace: 'nowrap' }}>Inverti</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={cancelWebcam}
                  style={{ ...circleButtonStyle, backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                />
                <span style={{ fontSize: 11, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', whiteSpace: 'nowrap' }}>Chiudi</span>
              </div>
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        ) : (
          <div style={videoContainerStyle}>
            <img
              src={capturedImage}
              alt="Captured"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
            />
            <div style={buttonsWrapperStyle}>
              {/* ✓ adds to staging and resets camera for next shot */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={addWebcamToStaging}
                  style={{ ...circleButtonStyle, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                />
                <span style={{ fontSize: 11, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', whiteSpace: 'nowrap' }}>Usa foto</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setCapturedImage(null)}
                  style={{ ...circleButtonStyle, backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
                />
                <span style={{ fontSize: 11, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', whiteSpace: 'nowrap' }}>Riscatta</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={cancelWebcam}
                  style={{ ...circleButtonStyle, backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                />
                <span style={{ fontSize: 11, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', whiteSpace: 'nowrap' }}>Chiudi</span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    )
  }

  const renderUploadModal = () => (
    <Modal
      title="📤 Aggiungi Foto"
      open={uploadModalVisible}
      onCancel={() => setUploadModalVisible(false)}
      footer={[
        <Button key="close" onClick={() => setUploadModalVisible(false)}>
          {stagedFiles.length > 0 ? `Chiudi (${stagedFiles.length} in attesa)` : 'Chiudi'}
        </Button>,
      ]}
    >
      <Upload.Dragger
        multiple
        accept="image/jpeg,image/png"
        fileList={[]}
        beforeUpload={(file) => {
          addFileToStaging(file)
          return false
        }}
        disabled={committing}
      >
        <p style={{ fontSize: '24px' }}>📁</p>
        <p>Trascina file qui o clicca per selezionare</p>
        <p style={{ fontSize: '12px', color: '#999' }}>
          JPG, PNG — le foto verranno caricate al salvataggio della scheda
        </p>
      </Upload.Dragger>

      {stagedFiles.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: token.colorTextSecondary, marginBottom: 8 }}>
            In attesa ({stagedFiles.length}):
          </div>
          <div style={{
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadiusSM,
            maxHeight: 240,
            overflowY: 'auto',
          }}>
            {stagedFiles.map((staged, idx) => (
              <div
                key={staged.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 10px',
                  borderBottom: idx < stagedFiles.length - 1 ? `1px solid ${token.colorBorder}` : 'none',
                }}
              >
                <img
                  src={staged.preview}
                  alt={staged.file.name}
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: 'cover',
                    borderRadius: token.borderRadiusSM,
                    flexShrink: 0,
                    background: token.colorBgContainer,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: token.colorText,
                    fontSize: 13,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {staged.file.name}
                  </div>
                  <div style={{ color: token.colorTextSecondary, fontSize: 11 }}>
                    {(staged.file.size / 1024).toFixed(0)} KB
                  </div>
                </div>
                <Tooltip title="Rimuovi dalla coda">
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    disabled={committing}
                    onClick={() => removeFromStaging(staged.id)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  )

  const renderThumbnailGrid = () => {
    if (loading) return <Spin />

    const visibleExisting = existingPhotos.filter(p => !photosToDelete.includes(p.id))
    const totalCount = visibleExisting.length + stagedFiles.length

    if (totalCount === 0) return <Empty description="Nessuna foto" />

    return (
      <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
        <div style={{ display: 'flex', gap: '8px', minWidth: 'min-content' }}>
          {/* Existing server-confirmed photos — blob URL autenticato via useAuthenticatedImage */}
          {visibleExisting.map(photo => (
            <ExistingPhotoCard
              key={photo.id}
              photo={photo}
              committing={committing}
              readOnly={readOnly}
              onSelect={() => {
                setSelectedPhotoForView(photo)
                setViewerModalVisible(true)
              }}
              onDelete={(e) => {
                e.stopPropagation()
                markForDeletion(photo.id)
              }}
            />
          ))}

          {/* Staged files — pending upload, shown with dashed blue border */}
          {stagedFiles.map(staged => (
            <div
              key={staged.id}
              style={{
                position: 'relative',
                width: '70px',
                height: '70px',
                flexShrink: 0,
                borderRadius: '6px',
                overflow: 'hidden',
                border: '2px dashed #1677ff',
                cursor: 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={staged.preview}
                alt={staged.file.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: token.colorBgMask,
                  color: token.colorTextLightSolid,
                  fontSize: '9px',
                  textAlign: 'center',
                  padding: '1px 0',
                  lineHeight: '14px',
                  pointerEvents: 'none',
                }}
              >
                nuova
              </div>
              <Tooltip title="Rimuovi dalla coda">
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  disabled={committing}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    padding: '2px',
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '4px',
                    fontSize: '10px',
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    removeFromStaging(staged.id)
                  }}
                />
              </Tooltip>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      {!compact && !readOnly && (
        <div style={{ marginBottom: '20px' }}>
          <Space>
            <Button
              type="primary"
              icon={<CameraOutlined />}
              onClick={() => {
                setWebcamModalVisible(true)
                setTimeout(() => startWebcam(), 100)
              }}
            >
              📸 Scatta Foto
            </Button>
            <Button icon={<CloudUploadOutlined />} onClick={() => setUploadModalVisible(true)}>
              📤 Carica Foto
            </Button>
          </Space>
        </div>
      )}

      <div style={{ marginTop: compact ? '0' : '20px' }}>
        {renderThumbnailGrid()}
      </div>

      {renderWebcamModal()}
      {renderUploadModal()}

      <Modal
        title="📸 Visualizza Foto"
        open={viewerModalVisible}
        onCancel={() => { setViewerModalVisible(false); setSelectedPhotoForView(null) }}
        width={800}
        footer={[
          <Button
            key="close"
            onClick={() => { setViewerModalVisible(false); setSelectedPhotoForView(null) }}
          >
            Chiudi
          </Button>,
        ]}
        bodyStyle={{ textAlign: 'center', padding: '20px' }}
      >
        {selectedPhotoForView && (
          <div>
            {/* Viewer: blob URL autenticato — useAuthenticatedImage(viewerUrl) chiamato in cima al componente */}
            {viewerImgLoading && (
              <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin size="large" />
              </div>
            )}
            {viewerBlobSrc && (
              <img
                src={viewerBlobSrc}
                alt={selectedPhotoForView.file_name}
                style={{ maxWidth: '100%', maxHeight: '600px', borderRadius: '8px', marginBottom: '16px' }}
              />
            )}
            {!viewerImgLoading && !viewerBlobSrc && (
              <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '13px' }}>
                ⚠️ Impossibile caricare l&apos;immagine
              </div>
            )}
            <div style={{ marginTop: '16px', color: '#333', fontSize: '13px' }}>
              <p style={{ margin: '4px 0' }}>{selectedPhotoForView.file_name}</p>
              <p style={{ margin: '4px 0', color: '#666' }}>
                {new Date(selectedPhotoForView.created_at).toLocaleDateString('it-IT')}{' '}
                {new Date(selectedPhotoForView.created_at).toLocaleTimeString('it-IT')}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export const PhotoCaptureManager = forwardRef(PhotoCaptureManagerComponent)
